import { useSelector } from "react-redux";
import {
    calculatePaymentInfoForSingleOffering,
    IPaymentInfoForSingleOffering,
    roundUp,
    SELECTORS,
    useEventDataOfSelectedEvent,
} from "@Utils";
import { getEventCurrency } from "@Modules/eventPage/utils/getEventCurrency";
import { getPricePerHead } from "@Modules/eventPage/utils/getInitialPrice";
import { getAreIntlPaymentsEnabled } from "@Modules/eventPage/utils/getAreIntlPaymentsEnabled";
import { getEarlyBirdObj } from "@Modules/eventPage/utils/earlyBird";
import { IEvent } from "@Interfaces";
import { IEarlyBird } from "../IEarlyBird";
import { getIsRenewBooking } from "../../RenewBookingForm/utils/getIsRenewBooking";
import { useEarlyBirdClock } from "./useEarlyBirdClock";
import { getHideEarlyBird } from "./getHideEarlyBird";

const useEarlyBird = ({
    urlQueryParams,
    disablePayment,
}: IEarlyBird.IProps) => {
    const { event, eventData } = useEventDataOfSelectedEvent();
    const host = useSelector(SELECTORS.host);
    const isRenewBooking = getIsRenewBooking({ urlQueryParams, eventData });

    const areIntlPaymentsEnabled: boolean = getAreIntlPaymentsEnabled({
        host,
        eventData,
    });

    const earlyBirdObj = getEarlyBirdObj({
        eventData,
        disablePayment,
        isRenewBooking,
        urlQueryParams,
        areIntlPaymentsEnabled,
    });

    const pricePerHead = getPricePerHead({ eventData });

    const { eventCurrencySymbol } = getEventCurrency({
        eventData,
    });

    const {
        paymentInfo: {
            slots,
            quantity,
            numberOfCustomers, // quantities of listings selected from upsell
        },

        discount: { discountData },
    }: IEvent.IStore = event;

    const computedPaymentValues: IPaymentInfoForSingleOffering.IReturn =
        calculatePaymentInfoForSingleOffering({
            eventData,
            quantity,
            slots,
            numberOfCustomers,
            discountData,
        });

    const {
        flags: {
            isSingleSlot, // Is it a single slot type
        },
        factors: {
            billingMultiplier, // Number of units being bought for this SKU
            earlyBirdScalingFactor,
        },
    } = computedPaymentValues;

    const earlyBirdPrice = earlyBirdObj?.price || 0;

    const discountedPrice =
        (isSingleSlot ? 1 : Math.max(billingMultiplier, 1)) *
        roundUp(pricePerHead - earlyBirdPrice * earlyBirdScalingFactor);

    const earlyBirdClockTimerString = useEarlyBirdClock(
        earlyBirdObj?.time || 0
    );

    const hideComponent = getHideEarlyBird({
        disablePayment,
        earlyBirdObj,
        urlQueryParams,
        eventData,
    });

    return {
        hideComponent,
        earlyBirdClockTimerString,
        earlyBirdText: earlyBirdObj?.text || "Early Bird Offer",
        earlyBirdDiscountString: `${eventCurrencySymbol} ${discountedPrice} OFF`,
    };
};

export default useEarlyBird;
